import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { fetchProjects } from '@/api/endpoints';
import { queryKeys } from '@/api/queryClient';
import { EmptyState, PageLoader, SearchField, SectionCard } from '@/components/ui';
import { helpAnchor } from '@/help/anchor';
import { useAttention, type AttentionItem } from '@/hooks';
import { currentProjectAtom } from '@/store';

import {
  formatCount,
  hasGaps,
  matchesSearch,
  percentOf,
  SORT_LABELS,
  sortProjects,
  sumUnits,
  type SortOrder,
} from './coverage';
import { CoverageLegend, CoverageMatrix } from './CoverageMatrix';

const ATTENTION_ID = 'needs-attention';

/**
 * Where things stand (spec §9.3): four headline numbers, the work that is blocked on somebody, and
 * one coverage matrix — a row per project, a column per locale.
 *
 * Coverage is counted in translation units — a plural key counts once per form the locale uses —
 * so the number matches what a translator actually has to type.
 */
export function DashboardPage() {
  const setCurrentProject = useSetAtom(currentProjectAtom);
  const attention = useAttention();
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState<SortOrder>('least-covered');
  const [onlyGaps, setOnlyGaps] = useState(false);
  const { data, isPending } = useQuery({
    queryKey: queryKeys.projects,
    queryFn: fetchProjects,
  });

  if (isPending) {
    return (
      <PageLoader />
    );
  }

  const projects = data ?? [];
  const units = sumUnits(projects);
  const missing = units.total - units.translated;
  const overall = percentOf(units);
  const rows = sortProjects(
    projects.filter((item) => matchesSearch(item, search) && (!onlyGaps || hasGaps(item))),
    order,
  );

  return (
    <>
      <Box data-help="dashboard-header" sx={{ mb: 3.5 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
          {TODAY.format(new Date())}
        </Typography>
        <Typography
          variant="h1"
          sx={{ fontSize: { xs: 28, md: 34 }, color: 'spectrum.mark' }}
        >
          {greeting(new Date().getHours())}
        </Typography>
        <Typography sx={{ fontSize: 15, color: 'text.secondary', mt: 1 }}>
          {attention.total
            ? `${attention.total} ${attention.total === 1 ? 'thing is' : 'things are'} waiting for you. Everything else is in order.`
            : 'Nothing is waiting for you. Here is where translation stands.'}
        </Typography>
      </Box>

      <Box
        data-help="dashboard-summary"
        data-testid="dashboard-summary"
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        <StatTile label="Projects" value={formatCount(projects.length)} />
        <StatTile
          label="Keys"
          value={formatCount(projects.reduce((sum, item) => sum + item.keyCount, 0))}
          caption="A shared key counts in each project"
        />
        <StatTile
          label="Translated"
          value={overall === null ? '—' : `${overall} %`}
          caption={
            overall === null
              ? 'Nothing to translate yet'
              : missing
                ? `${formatCount(missing)} values still missing`
                : 'Every target locale is complete'
          }
        />
        <StatTile
          label="Needs attention"
          value={formatCount(attention.total)}
          caption={attention.total ? 'Blocked on a decision — see below' : 'Nothing waiting'}
          tone={attention.total ? 'act' : undefined}
          onClick={
            attention.total
              ? () =>
                  document
                    .getElementById(ATTENTION_ID)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              : undefined
          }
        />
      </Box>

      {attention.items.length ? (
        <Paper
          id={ATTENTION_ID}
          variant="outlined"
          data-help="dashboard-attention"
          sx={{ mb: 3, overflow: 'hidden', scrollMarginTop: 84 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 2.5,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box
              aria-hidden="true"
              sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'warning.main' }}
            />
            <Typography variant="h3">Needs attention</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {attention.items.length} {attention.items.length === 1 ? 'item' : 'items'} · blocked on
              a decision
            </Typography>
          </Box>
          {attention.items.map((item, index) => (
            <AttentionRow key={`${item.kind}-${item.to}`} item={item} first={index === 0} />
          ))}
        </Paper>
      ) : null}

      {projects.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="You have no role on any project. An administrator grants access per project."
        />
      ) : (
        <SectionCard
          flush
          title="Coverage"
          description="Share of each locale's values already written — machine, draft or reviewed. Click a cell to list what is missing."
          helpId="dashboard-projects"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              p: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search by code or name"
              helpId="dashboard-search"
            />
            <TextField
              select
              label="Sort"
              value={order}
              onChange={(event) => setOrder(event.target.value as SortOrder)}
              data-help="dashboard-sort"
              sx={{ minWidth: 180 }}
            >
              {Object.entries(SORT_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch checked={onlyGaps} onChange={(event) => setOnlyGaps(event.target.checked)} />
              }
              label="Only with gaps"
              data-help="dashboard-only-gaps"
              slotProps={{ typography: { sx: { fontSize: 13 } } }}
            />
            <Box sx={{ flex: 1 }} />
            <CoverageLegend />
          </Box>

          {rows.length ? (
            <CoverageMatrix items={rows} onOpenProject={setCurrentProject} />
          ) : (
            <EmptyState
              title="No projects"
              description={
                onlyGaps && !search.trim()
                  ? 'Every project is fully translated.'
                  : 'Nothing matches that search.'
              }
            />
          )}
        </SectionCard>
      )}
    </>
  );
}

/**
 * A headline number, in the light weight and the blue of the design language. A tile that leads
 * somewhere is a button so the keyboard reaches it too. `act` turns the caption orange: the one
 * colour that means somebody has to do something.
 */
function StatTile({
  label,
  value,
  caption,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  caption?: string;
  tone?: 'act';
  onClick?: () => void;
}) {
  const body: ReactNode = (
    <>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.75 }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: 26,
          fontWeight: 300,
          lineHeight: 1.1,
          color: 'spectrum.mark',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
      {caption ? (
        <Typography
          sx={{
            fontSize: 12,
            mt: 0.75,
            color: tone === 'act' ? 'spectrum.act.ink' : 'text.secondary',
            fontWeight: tone === 'act' ? 500 : 400,
          }}
        >
          {caption}
        </Typography>
      ) : null}
    </>
  );

  return (
    <Paper variant="outlined" sx={{ display: 'flex', overflow: 'hidden' }}>
      {onClick ? (
        <ButtonBase
          onClick={onClick}
          sx={{
            flex: 1,
            // A button centres its content; the tile reads top-down like its neighbours.
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            textAlign: 'left',
            px: 2,
            py: 1.75,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {body}
        </ButtonBase>
      ) : (
        <Box sx={{ flex: 1, px: 2, py: 1.75 }}>{body}</Box>
      )}
    </Paper>
  );
}

/** One line per item: the icon and the label say how bad it is, so the colour is not alone. */
function AttentionRow({ item, first }: { item: AttentionItem; first: boolean }) {
  const failed = item.kind === 'release';
  const Icon = failed ? ErrorOutlineOutlinedIcon : WarningAmberOutlinedIcon;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 1.75,
        borderTop: first ? 0 : 1,
        borderColor: 'spectrum.rowLine',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 36,
          height: 36,
          flex: 'none',
          borderRadius: 2,
          display: 'grid',
          placeItems: 'center',
          bgcolor: (theme) =>
            failed ? alpha(theme.palette.error.main, 0.1) : theme.palette.spectrum.act.tint,
          color: failed ? 'error.main' : 'spectrum.act.ink',
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 500 }}>{item.label}</Typography>
        <Typography noWrap sx={{ fontSize: 13, color: 'text.secondary' }}>
          {item.detail}
        </Typography>
      </Box>
      <Button
        component={Link}
        to={item.to}
        variant="outlined"
        size="small"
        data-help={helpAnchor('dashboard-attention-open')}
      >
        Open
      </Button>
    </Box>
  );
}

const TODAY = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** The display line of the page: calm, and true at any hour somebody opens it. */
function greeting(hour: number): string {
  if (hour < 5) return 'Good evening.';
  if (hour < 12) return 'Good morning.';
  if (hour < 18) return 'Good afternoon.';
  return 'Good evening.';
}
